<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { Banknote, CreditCard, Calculator, OctagonX, WalletCards, Pencil } from 'lucide-vue-next';
import {
    RadioGroup,
    RadioGroupItem,
} from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import type { PaymentMethodType } from '@lib/types/paymentMethodType';

const props = defineProps<{
    methods: PaymentMethodType[];
    modelValue?: string | undefined;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | undefined): void;
}>();

const selectedId = useVModel(props, 'modelValue', emit, {
    passive: true,
    defaultValue: undefined,
});

const icons = [
    { name: 'CreditCard', component: CreditCard },
    { name: 'Banknote', component: Banknote },
    { name: 'Calculator', component: Calculator },
]

const selectedPaymentMethod = computed(() => {
    return props.methods.find(method => method.id === selectedId.value);
});

onMounted(async () => {
    // Auto-select first method
    if (!selectedId.value && props.methods[0]?.id) {
        selectedId.value = props.methods[0].id;
    }
});

watch(() => props.methods, (methods) => {
    if (!selectedId.value && methods[0]?.id) {
        selectedId.value = methods[0].id;
    }
});

const isPaymentOptionsOpen = ref(false);

const selectMethod = async (id: string) => {
    selectedId.value = id;
    isPaymentOptionsOpen.value = false;
};
</script>

<template>
    <div class="my-4">
        <Collapsible v-if="methods.length > 0"
                     v-model:open="isPaymentOptionsOpen"
                     class="rounded-lg border bg-card text-card-foreground shadow-sm">
            <CollapsibleTrigger class="w-full p-4 hover:bg-card/80 cursor-pointer flex items-center justify-between">
                <div>
                    <h3 class="font-bold text-xl mb-2 text-left">Payment</h3>
                    <div class="font-medium flex items-center">
                        <component :is="icons.find(icon => icon.name === selectedPaymentMethod?.icon)?.component || WalletCards"
                                   class="mr-2 flex-shrink-0" />
                        {{ selectedPaymentMethod?.name }}
                    </div>
                    <div class="text-muted-foreground text-sm leading-snug"
                         v-if="selectedPaymentMethod?.description">
                        {{ selectedPaymentMethod?.description }}
                    </div>
                </div>
                <Pencil class="ml-2 flex-shrink-0" />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div class="px-4 pb-4">
                    <RadioGroup :model-value="selectedId"
                                @update:model-value="selectMethod">
                        <Label v-for="method in methods"
                               :key="method.id"
                               class="cursor-pointer hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-4 mb-2 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/10 dark:has-[[data-state=checked]]:border-primary-foreground dark:has-[[data-state=checked]]:bg-primary/20">
                            <RadioGroupItem :id="method.id"
                                            :value="method.id"
                                            class="shadow-none data-[state=checked]:border-primary data-[state=checked]:bg-primary *:data-[slot=radio-group-indicator]:[&>svg]:fill-white *:data-[slot=radio-group-indicator]:[&>svg]:stroke-white" />
                            <div class="grid gap-1 font-normal">
                                <div class="font-medium flex items-center">
                                    <component :is="icons.find(icon => icon.name === method.icon)?.component || WalletCards"
                                               class="mr-2" />
                                    {{ method.name }}
                                </div>
                                <div class="text-muted-foreground text-sm leading-snug"
                                     v-if="method.description">
                                    {{ method.description }}
                                </div>
                            </div>
                        </Label>
                    </RadioGroup>
                </div>
            </CollapsibleContent>
        </Collapsible>
        <div v-else
             class="p-2 bg-destructive text-destructive-foreground rounded-lg">
            <OctagonX class="mr-1 inline-block" />
            No payment options found.
            <br>
            This could be a temporary issue.
            <br>
            Please try again later.
        </div>
    </div>
</template>
